import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, TrendingUp, Calendar, AlertCircle, Link as LinkIcon, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { nanoid } from "nanoid";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  computeDashboardStats,
  type DashboardStats,
  type LenderDashboardData,
} from "./lenderDashboardStats";

interface LenderDashboardProps {
  lenderProfileId?: string;
  /** Rows already fetched for the public page; when set, nothing is read here. */
  data?: LenderDashboardData;
}

export const LenderDashboard = ({ lenderProfileId, data }: LenderDashboardProps = {}) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAccessLinkDialog, setShowAccessLinkDialog] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [expirationDays, setExpirationDays] = useState(90);
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (data) {
      setStats(computeDashboardStats(data));
      setLoading(false);
      return;
    }
    fetchDashboardData();
  }, [data]);

  const fetchDashboardData = async () => {
    try {
      let lendersQuery = supabase
        .from("lender_profiles")
        .select("id, lender_name, created_at")
        .eq("is_active", true);

      if (lenderProfileId) {
        lendersQuery = lendersQuery.eq("id", lenderProfileId);
      }

      const { data: lenders, error: lendersError } = await lendersQuery;

      if (lendersError) throw lendersError;

      let transactionsQuery = supabase
        .from("lender_transactions")
        .select("lender_profile_id, transaction_type, amount, transaction_date");

      if (lenderProfileId) {
        transactionsQuery = transactionsQuery.eq("lender_profile_id", lenderProfileId);
      }

      const { data: transactions, error: txnError } = await transactionsQuery;

      if (txnError) throw txnError;

      setStats(computeDashboardStats({ lenders: lenders ?? [], transactions: transactions ?? [] }));
    } catch (error) {
      toast({
        title: "Error",
        description: (error as { message?: string })?.message ?? String(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAccessLink = async () => {
    if (!accessEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = nanoid(32);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expirationDays);

      const { error } = await supabase
        .from("lender_dashboard_access")
        .insert({
          email: accessEmail,
          lender_profile_id: null, // null = shows all lenders
          access_token: token,
          expires_at: expiresAt.toISOString(),
        });

      if (error) throw error;

      const link = `${window.location.origin}/lender-dashboard?token=${token}`;
      setGeneratedLink(link);
      toast({
        title: "Success",
        description: "Dashboard access link generated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: (error as { message?: string })?.message ?? String(error),
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast({
      title: "Success",
      description: "Link copied to clipboard",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const resetLinkDialog = () => {
    setShowAccessLinkDialog(false);
    setAccessEmail("");
    setExpirationDays(90);
    setGeneratedLink("");
    setCopied(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8">No data available</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {lenderProfileId ? "Your Lender Dashboard" : "Lender Dashboard"}
          </h3>
          {/* {!lenderProfileId && (
            <Button onClick={() => setShowAccessLinkDialog(true)} variant="outline">
              <LinkIcon className="h-4 w-4 mr-2" />
              Generate Dashboard Access Link
            </Button>
          )} */}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <AlertCircle className="h-4 w-4" />
              <span>Active Lenders</span>
            </div>
            <p className="text-2xl font-bold">{stats.totalLenders}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span>Total Funds Received</span>
            </div>
            <p className="text-2xl font-bold">
              ${stats.totalFundsReceived.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span>Total Funds Paid</span>
            </div>
            <p className="text-2xl font-bold">
              ${stats.totalFundsPaid.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </p>
          </Card>

          <Card className="p-4 bg-primary/5">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span>Total Accrued Interest</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              ${stats.totalAccruedInterest.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </p>
          </Card>
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-3">Lender Details</h4>
        <div className="space-y-3">
          {stats.lenderStats.map((lender, idx) => (
            <Card key={idx} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="font-semibold">{lender.lender_name}</h5>
                  <p className="text-sm text-muted-foreground capitalize">
                    {lender.payment_schedule} payments
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Principal</p>
                  <p className="font-semibold">
                    ${lender.principal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Accrued Interest</p>
                  <p className="font-semibold text-primary">
                    ${lender.accrued_interest.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Next Payment</p>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <p className="font-semibold">
                      {new Date(lender.next_payment_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={showAccessLinkDialog} onOpenChange={(open) => !open && resetLinkDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Full Dashboard Access Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!generatedLink ? (
              <>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={accessEmail}
                    onChange={(e) => setAccessEmail(e.target.value)}
                    placeholder="recipient@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="expiration">Expiration (days)</Label>
                  <Input
                    id="expiration"
                    type="number"
                    value={expirationDays}
                    onChange={(e) => setExpirationDays(parseInt(e.target.value))}
                    min={1}
                    max={365}
                  />
                </div>
                <Button onClick={generateAccessLink} className="w-full">
                  Generate Link
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Access Link</Label>
                  <div className="flex gap-2">
                    <Input value={generatedLink} readOnly />
                    <Button onClick={copyToClipboard} variant="outline" size="icon">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This link will expire in {expirationDays} days and shows all lenders' financial data
                  </p>
                </div>
                <Button onClick={resetLinkDialog} variant="outline" className="w-full">
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
